import { Check, Loader2, Sparkles } from 'lucide-react';
import type { AiPreviewOrientation } from '../AIPreview.types';

const OPTIONS: Array<{ value: AiPreviewOrientation; label: string; hint: string; frame: string }> = [
  { value: 'LANDSCAPE', label: 'Landscape', hint: 'Wide screens in malls, markets and shops', frame: 'aspect-[16/9] w-9' },
  { value: 'PORTRAIT', label: 'Portrait', hint: 'Tall screens on roads, lakesides and kiosks', frame: 'aspect-[9/16] h-8' },
];

type OrientationPickerProps = {
  /** The selected orientations: one, or both. */
  values: AiPreviewOrientation[];
  recommended: AiPreviewOrientation;
  loading: boolean;
  onChange: (value: AiPreviewOrientation) => void;
};

export function OrientationPicker({ values, recommended, loading, onChange }: OrientationPickerProps) {
  return (
    <section className="camp-section text-left">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div>
          <h2 className="m-0 text-[15px] font-semibold text-[var(--dash-ink)]">Screen orientation</h2>
          <p className="m-0 mt-0.5 text-xs text-[var(--dash-muted)]">Pick one, or both to run on landscape and portrait screens together.</p>
        </div>
        {loading ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[var(--dash-brand)]" role="status">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Finding the best screens…
          </span>
        ) : null}
      </div>

      <div role="group" aria-label="Screen orientation" className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {OPTIONS.map((option) => {
          const selected = values.includes(option.value);
          // The last selected orientation cannot be switched off: a plan needs at least one.
          const onlyOne = selected && values.length === 1;
          return (
            <button
              key={option.value}
              type="button"
              role="checkbox"
              aria-checked={selected}
              disabled={loading}
              title={onlyOne ? 'Keep at least one orientation selected' : undefined}
              onClick={() => onChange(option.value)}
              className={`camp-mode-card flex items-center gap-3 text-left disabled:cursor-wait ${
                selected ? 'ring-2 ring-[var(--dash-brand)]/40' : ''
              }`}
            >
              <span className="flex h-12 w-14 shrink-0 items-center justify-center rounded-lg bg-[var(--dash-soft)]">
                <span className={`${option.frame} rounded-[3px] border-2 border-[var(--dash-brand)] bg-white`} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-bold text-[var(--dash-ink)]">{option.label}</span>
                  {recommended === option.value ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[var(--dash-brand-soft)] px-2 py-0.5 text-[10px] font-bold text-[var(--dash-brand)]">
                      <Sparkles className="h-3 w-3" />
                      AI recommended
                    </span>
                  ) : null}
                </span>
                <span className="mt-0.5 block text-xs text-[var(--dash-muted)]">{option.hint}</span>
              </span>
              {selected ? (
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[var(--dash-brand)] text-white">
                  <Check className="h-3 w-3" strokeWidth={3} />
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </section>
  );
}
